import { useEffect, useState } from 'react';
import { useStateInRef } from 'seagull/utils/useStateInRef';
import { useIoContext, useStore } from '~/contexts';
import { useBootStalled } from '~/hooks/useBootStalled';
import { selectors } from '~/store/state-selectors';
const resolveDismissSignal = (param)=>{
    let { authFlowDone, bootFailedOnNetwork, bootStalled } = param;
    if (authFlowDone) return 'auth-flow-done';
    if (bootFailedOnNetwork) return 'boot-network-failure';
    if (bootStalled) return 'boot-stalled';
    return null;
};
const resolvedWithin = (settled, timeoutMs)=>new Promise((resolve)=>{
        const timer = setTimeout(resolve, timeoutMs);
        void settled.catch(()=>{}).then(()=>{
            clearTimeout(timer);
            resolve();
        });
    });
export function useNativeSplashOverlay(param) {
    let { fadeOutMs, hideSafetyMs, stalledAfterMs } = param;
    const { native } = useIoContext();
    const authFlowDone = useStore((state)=>state.authFlowDone);
    const bootFailedOnNetwork = useStore(selectors.isBootNetworkFailure);
    const bootStalled = useBootStalled(stalledAfterMs);
    const [phase, setPhase] = useState('looping');
    const [dismissSignal, setDismissSignal] = useState(null);
    const [animationData, setAnimationData] = useState(null);
    const getMarkBootedAfterFirstLaunchUpdate = useStateInRef(markBootedAfterFirstLaunchUpdate);
    useEffect(()=>{
        let active = true;
        void import('../anchor-splash.json').then((module)=>{
            if (active) setAnimationData(module.default);
        }).catch(()=>{});
        return ()=>{
            active = false;
        };
    }, []);
    useEffect(()=>{
        const timer = setTimeout(()=>native.boot.markBooted('splash-safety-timeout'), hideSafetyMs);
        return ()=>clearTimeout(timer);
    }, [
        hideSafetyMs
    ]);
    useEffect(()=>{
        const signal = resolveDismissSignal({
            authFlowDone,
            bootFailedOnNetwork,
            bootStalled
        });
        if (dismissSignal == null && signal != null) {
            setDismissSignal(signal);
        }
    }, [
        authFlowDone,
        bootFailedOnNetwork,
        bootStalled,
        dismissSignal
    ]);
    useEffect(()=>{
        let fadeTimer;
        let active = true;
        if (dismissSignal != null) {
            void getMarkBootedAfterFirstLaunchUpdate()(dismissSignal).then(()=>{
                if (active) {
                    setPhase('fading');
                    fadeTimer = setTimeout(()=>setPhase('done'), fadeOutMs);
                }
            }).catch(()=>{});
        }
        return ()=>{
            active = false;
            clearTimeout(fadeTimer);
        };
    }, [
        dismissSignal,
        fadeOutMs
    ]);
    // WHY: a first-launch OTA reloads the WebView — hiding early shows the shipped bundle mid-swap.
    async function markBootedAfterFirstLaunchUpdate(signal) {
        await resolvedWithin(native.ota.whenFirstLaunchUpdateSettled(), hideSafetyMs);
        native.boot.markBooted(signal);
    }
    const onFirstFramePainted = ()=>void markBootedAfterFirstLaunchUpdate('first-frame-painted').catch(()=>{});
    return {
        phase,
        animationData,
        onFirstFramePainted
    };
}
